import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Clock, X } from '../icons'
import { useSchedules } from '../hooks/useSchedules'
import { api } from '../lib/api'
import { useToasts } from '../hooks/useToasts'
import type { Day, Device, ScheduleBand, ScheduleView } from '../types/device'

interface Props {
  device: Device
  /** All known devices — used to pick copy-to targets (filtered to switches). */
  allDevices: Device[]
  asSheet: boolean
  onClose: () => void
}

// Italian convention: Monday-first. API uses Sunday-based keys.
const DAY_LABELS_IT = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'] as const
const DAY_KEYS: Day[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const MAX_BANDS = 10

function makeDefaultBand(): ScheduleBand {
  return { onAt: '09:00', offAt: '17:00', days: ['MON', 'TUE', 'WED', 'THU', 'FRI'] }
}

interface DraftBand {
  /** Stable React key — doesn't depend on jobIds which may not be set on a new band. */
  uid: string
  onAt: string
  offAt: string
  days: Day[]
}

function bandsFromView(view: ScheduleView | undefined): DraftBand[] {
  if (!view) return []
  return view.bands.map((b, i) => ({
    uid: `${b.onJobId ?? 'new'}-${b.offJobId ?? 'new'}-${i}`,
    onAt: b.onAt,
    offAt: b.offAt,
    days: b.days,
  }))
}

function draftsToPayload(channel: number, drafts: DraftBand[]): ScheduleView {
  return {
    channel,
    bands: drafts.map(({ onAt, offAt, days }) => ({ onAt, offAt, days })),
  }
}

function validate(drafts: DraftBand[]): string | null {
  for (let i = 0; i < drafts.length; i++) {
    const b = drafts[i]
    if (!/^\d{2}:\d{2}$/.test(b.onAt) || !/^\d{2}:\d{2}$/.test(b.offAt)) {
      return `Fascia ${i + 1}: inserisci orari validi (HH:MM)`
    }
    if (b.onAt === b.offAt) {
      return `Fascia ${i + 1}: l'orario di accensione e spegnimento devono essere diversi`
    }
    if (b.days.length === 0) {
      return `Fascia ${i + 1}: seleziona almeno un giorno`
    }
  }
  return null
}

/** Devices that can carry a "switch" schedule — exclude the source + meters/sensors. */
function copyCandidates(allDevices: Device[], sourceId: string): Device[] {
  return allDevices
    .filter((d) => d.id !== sourceId)
    .filter((d) => d.kind === 'switch' || d.kind === 'switch-metered')
    .filter((d) => !!d.relay) // must have a relay channel
    .sort((a, b) => a.name.localeCompare(b.name, 'it'))
}

export function ProgrammazioneModal({ device, allDevices, asSheet, onClose }: Props) {
  const { data, isLoading } = useSchedules(device.id, true)
  const qc = useQueryClient()
  const { push } = useToasts()

  const [drafts, setDrafts] = useState<DraftBand[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Copy-to-other-devices state
  const [copyOpen, setCopyOpen] = useState(false)
  const [copyTargets, setCopyTargets] = useState<Set<string>>(new Set())
  const candidates = useMemo(() => copyCandidates(allDevices, device.id), [allDevices, device.id])

  useEffect(() => {
    if (drafts !== null) return // already initialized
    if (isLoading) return
    setDrafts(bandsFromView(data))
  }, [data, isLoading, drafts])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const titleId = useMemo(() => `prog-title-${device.id}`, [device.id])

  const updateBand = (uid: string, patch: Partial<Omit<DraftBand, 'uid'>>) => {
    setDrafts((prev) => prev?.map((b) => (b.uid === uid ? { ...b, ...patch } : b)) ?? null)
    setError(null)
  }
  const toggleDay = (uid: string, day: Day) => {
    setDrafts((prev) =>
      prev?.map((b) =>
        b.uid === uid
          ? { ...b, days: b.days.includes(day) ? b.days.filter((d) => d !== day) : [...b.days, day] }
          : b,
      ) ?? null,
    )
    setError(null)
  }
  const addBand = () => {
    setDrafts((prev) => {
      if (!prev) return prev
      if (prev.length >= MAX_BANDS) return prev
      return [...prev, { uid: `new-${Date.now()}-${prev.length}`, ...makeDefaultBand() }]
    })
    setError(null)
  }
  const removeBand = (uid: string) => {
    setDrafts((prev) => prev?.filter((b) => b.uid !== uid) ?? null)
    setError(null)
  }
  const toggleTarget = (id: string) => {
    setCopyTargets((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const onBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const save = async () => {
    if (!drafts) return
    const v = validate(drafts)
    if (v) { setError(v); return }
    setSubmitting(true)

    const payload = draftsToPayload(data?.channel ?? 0, drafts)
    const targetIds = [device.id, ...copyTargets]

    // Fan out in parallel — each save is independent. Promise.allSettled so
    // one failure doesn't cancel the others.
    const results = await Promise.allSettled(
      targetIds.map((id) => api.saveSchedules(id, payload)),
    )
    setSubmitting(false)

    const succeeded: string[] = []
    const failed: Array<{ id: string; reason: string }> = []
    results.forEach((r, i) => {
      const id = targetIds[i]
      if (r.status === 'fulfilled') succeeded.push(id)
      else failed.push({ id, reason: (r.reason as Error)?.message ?? 'errore' })
    })

    // Invalidate the schedules query for every device we touched so the
    // detail panel re-renders.
    succeeded.forEach((id) => qc.invalidateQueries({ queryKey: ['schedules', id] }))

    if (failed.length === 0) {
      const total = succeeded.length
      push('success', total === 1 ? 'Programmazione salvata' : `Programmazione salvata su ${total} dispositivi`)
      onClose()
      return
    }

    // Partial or total failure — list the names of failures
    const nameById = new Map(allDevices.map((d) => [d.id, d.name]))
    const failedNames = failed.map((f) => nameById.get(f.id) ?? f.id).join(', ')
    if (succeeded.length === 0) {
      push('error', `Errore salvataggio: ${failedNames}`)
    } else {
      push('error', `Salvataggio parziale (${succeeded.length}/${targetIds.length}). Errore su: ${failedNames}`)
      onClose()
    }
  }

  const targetCount = copyTargets.size
  const totalDevices = 1 + targetCount
  const saveLabel = submitting
    ? 'Salvataggio…'
    : targetCount === 0
      ? 'Salva'
      : `Salva (${totalDevices} dispositivi)`

  return createPortal(
    <div
      className={`modal-overlay${asSheet ? ' modal-overlay--sheet' : ''}`}
      style={{ zIndex: 70 }}
      onClick={onBackdrop}
    >
      <div
        className={`modal modal--wide${asSheet ? ' modal--sheet' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {asSheet && <div className="modal__grabber" aria-hidden="true" />}
        <div className="modal__header">
          <div className="modal__title-wrap">
            <div className="modal__icon"><Clock /></div>
            <h2 className="modal__title" id={titleId}>
              Programmazione <span className="dim">— {device.name}</span>
            </h2>
          </div>
          <button className="modal__close" onClick={onClose} aria-label="Chiudi">
            <X />
          </button>
        </div>

        <div className="modal__body">
          {isLoading || drafts === null ? (
            <div className="sk" style={{ width: '100%', height: 160, borderRadius: 10 }} />
          ) : drafts.length === 0 ? (
            <div className="band-empty">
              <div className="band-empty__title">Nessuna fascia oraria</div>
              <div className="band-empty__sub">Aggiungi un intervallo per programmare accensione e spegnimento automatici.</div>
              <button type="button" className="btn btn--primary" onClick={addBand}>
                + Aggiungi fascia
              </button>
            </div>
          ) : (
            <>
              {drafts.map((b, i) => (
                <div className="band-card" key={b.uid}>
                  <div className="band-card__header">
                    <span className="band-card__title">Fascia {i + 1}</span>
                    <button
                      type="button"
                      className="band-card__remove"
                      onClick={() => removeBand(b.uid)}
                      aria-label={`Rimuovi fascia ${i + 1}`}
                      title="Rimuovi"
                    >
                      <X />
                    </button>
                  </div>

                  <div className="field-row">
                    <div className="field">
                      <label htmlFor={`on-${b.uid}`}>Accendi alle</label>
                      <input
                        id={`on-${b.uid}`}
                        type="time"
                        value={b.onAt}
                        onChange={(e) => updateBand(b.uid, { onAt: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`off-${b.uid}`}>Spegni alle</label>
                      <input
                        id={`off-${b.uid}`}
                        type="time"
                        value={b.offAt}
                        onChange={(e) => updateBand(b.uid, { offAt: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="days-legend">Giorni</div>
                    <div className="day-chips">
                      {DAY_LABELS_IT.map((label, j) => {
                        const key = DAY_KEYS[j]
                        const selected = b.days.includes(key)
                        return (
                          <button
                            key={label}
                            type="button"
                            className="day-chip"
                            data-selected={selected}
                            onClick={() => toggleDay(b.uid, key)}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {drafts.length < MAX_BANDS && (
                <button type="button" className="band-add" onClick={addBand}>
                  + Aggiungi fascia
                </button>
              )}

              {/* Copy-to-other-devices section */}
              {candidates.length > 0 && (
                <div className="copy-section">
                  <button
                    type="button"
                    className="copy-section__toggle"
                    onClick={() => setCopyOpen((v) => !v)}
                    aria-expanded={copyOpen}
                  >
                    <span>{copyOpen ? '▼' : '▶'}</span>
                    <span>Applica anche ad altri dispositivi</span>
                    {targetCount > 0 && (
                      <span className="count-pill count-pill--lime">{targetCount}</span>
                    )}
                  </button>
                  {copyOpen && (
                    <div className="copy-section__body">
                      <div className="copy-section__hint">
                        La stessa programmazione (tutte le fasce) verrà salvata sui dispositivi selezionati.
                        Le programmazioni esistenti su quei dispositivi saranno sostituite.
                      </div>
                      <div className="copy-targets">
                        {candidates.map((c) => {
                          const selected = copyTargets.has(c.id)
                          return (
                            <label key={c.id} className="copy-target" data-selected={selected}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleTarget(c.id)}
                              />
                              <span className="copy-target__name">{c.name}</span>
                              {c.isProtected && (
                                <span className="copy-target__tag">Riservato</span>
                              )}
                              {!c.online && (
                                <span className="copy-target__tag copy-target__tag--muted">Offline</span>
                              )}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && <div className="modal__error" role="alert">{error}</div>}

              <div className="modal__helper">
                Le fasce vengono eseguite direttamente sul dispositivo. Le fasce che attraversano la mezzanotte
                (es. 17:30–00:30) sono gestite automaticamente: lo spegnimento avviene il giorno successivo.
              </div>
            </>
          )}
        </div>

        <div className="modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={submitting}>Annulla</button>
          <button
            className="btn btn--primary"
            onClick={save}
            disabled={!drafts || drafts.length === 0 || submitting}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
